import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import { PopupMatch2BaseInitOptions } from '../components/PopupMatch2Base';
import { Match2TournamentTimer2 } from './Match2TournamentTimer2';
import Scroll from 'src/game/components/shared/Scroll';
import {
  Match2TournamentEntry,
  Match2TournamentEntryParams,
} from './Match2TournamentEntry';
import {
  Match2ModelTournamentData,
  Match2ModelTournamentPlayer,
} from 'src/replicant/match2/tournament/match2-tournament.actions';
import sounds from 'src/lib/sounds';
import { AssetGroup, engine } from '@play-co/timestep-core';
import PopupMatch2BaseFullscreen from '../components/PopupMatch2BaseFullscreen';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Match2TournamentStreakBar from './Match2TournamentStreakBar';
import Match2TournamentResultRank from './Match2TournamentResultRank';
import Match2TournamentResultReward from './Match2TournamentResultReward';
import { match2TournamentRuleset } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import {
  getStreakPopupClaimTitleLayout,
  getStreakPopupTitleLayout,
  getStreakTournamentBannerImage,
  getTournamentTitleImage,
  isFlyingHighRedesignTournament,
} from './match2-tournament.utils';

interface PopupMatch2TournamentInitOptions extends PopupMatch2BaseInitOptions {
  tournamentData: Match2ModelTournamentData;
}

const entryScale = 1.25;

export default class PopupMatch2TournamentStreak extends PopupMatch2BaseFullscreen {
  private readonly scroll: Scroll<
    Match2TournamentEntryParams,
    Match2TournamentEntry
  >;
  private readonly header: ImageScaleView;
  private readonly title: ImageScaleView;
  private readonly message: LangBitmapFontTextView;
  private readonly mainPlayer: Match2TournamentEntry;
  private readonly timer: Match2TournamentTimer2;
  private readonly streak: Match2TournamentStreakBar;
  private resultRank!: Match2TournamentResultRank;
  private resultReward!: Match2TournamentResultReward;
  private readonly scrollArea: ImageScaleView;
  private tournamentData?: Match2ModelTournamentData;
  private shouldClaimRewards = false;
  private tickHandler: () => void = () => void 0;
  private playerRank?: number;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.TOURNAMENT.POPUP,
      },
      bgColor: '#0F2794',
      cta: {
        footerHeight: 170,
        onClick: async () => {
          opts.close(true);
        },
      },
    });

    const { width, height } = this.getView().style;
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';
    const titleLayout = getStreakPopupTitleLayout(locale);

    this.header = new ImageScaleView({
      superview: this.panel,
      image: getStreakTournamentBannerImage('ongoing'),
      width: 720,
      height: 465,
    });

    this.title = new ImageScaleView({
      superview: this.header,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: getTournamentTitleImage('streak', locale),
      centerOnOrigin: true,
      x: this.header.style.width * 0.5,
      ...titleLayout,
    });

    this.timer = new Match2TournamentTimer2({
      superview: this.panel,
      x: width * 0.5,
      y: 245,
      centerOnOrigin: true,
    });

    this.message = new LangBitmapFontTextView({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: 315,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 27,
      font: 'Title',
      width: this.panel.style.width * 0.85,
      height: 100,
      color: '#FFFFFF',
      localeText: () => i18n('match2.tournament.info_streak'),
    });

    this.streak = new Match2TournamentStreakBar({
      superview: this.header,
      x: this.header.style.width * 0.5,
      y: 410,
    });

    this.resultRank = new Match2TournamentResultRank(
      {
        superview: this.header,
        x: 160,
        y: 220,
        visible: false,
      },
      'match2.tournament.rank',
    );

    this.resultReward = new Match2TournamentResultReward(
      {
        superview: this.header,
        x: this.header.style.width - 190,
        y: 220,
        visible: false,
      },
      'match2.tournament.reward',
    );

    const scrollHeight =
      height - this.footerHeight - this.header.style.height + 5;

    this.scrollArea = new ImageScaleView({
      superview: this.panel,
      width,
      height: scrollHeight,
      y: this.header.style.height,
      backgroundColor: '#FF0000',
      visible: false,
    });

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: this.scrollArea.style.x + 8,
        y: this.scrollArea.style.y + 5,
        width: this.scrollArea.style.width - 10,
        height: this.scrollArea.style.height - 10,
      },
      margin: 30,
      firstItemOffset: -15,
      lastItemOffset: -15,
    });

    this.mainPlayer = new Match2TournamentEntry(
      {
        superview: this.panel,
        width: (width + 30) / entryScale,
        height: 102,
        scale: entryScale,
        x: this.scrollArea.style.x - 15,
        y: this.scrollArea.style.y + this.scrollArea.style.height,
      },
      'streak',
    );
  }

  public init(args: PopupMatch2TournamentInitOptions) {
    super.init(args);
    this.tournamentData = args.tournamentData;
    this.timer.setTournamentData(this.tournamentData);
    this.playerRank = this.tournamentData.currentRank;
    this.scroll.setItems(this.getEntriesFromEvent(this.tournamentData));
    this.mainPlayer.setup({
      id: this.tournamentData.player.id,
      name: this.getPlayerName(this.tournamentData.player),
      avatar: this.getPlayerAvatar(this.tournamentData.player),
      frame: this.tournamentData.player.frame,
      score: this.tournamentData.player.score,
      scoreUpdatedAt: this.tournamentData.player.scoreUpdatedAt,
      position: this.tournamentData.currentRank,
      alt: true,
    });

    this.updateState();
  }

  private getEntriesFromEvent(event: Match2ModelTournamentData) {
    const mainPlayerId = StateObserver.getState().user.id;
    const entries: Match2TournamentEntryParams[] = [];
    for (const player of event.leaderboard) {
      entries.push({
        id: player.id,
        name: this.getPlayerName(player),
        avatar: this.getPlayerAvatar(player),
        frame: player.frame,
        score: player.score,
        scoreUpdatedAt: player.scoreUpdatedAt,
        position: 0,
        me: player.id === mainPlayerId,
      });
    }

    return entries;
  }

  private getPlayerName(player: Match2ModelTournamentPlayer) {
    const user = StateObserver.getState().user;
    if (!player || !player.id || player.id === user.id) {
      // Get name from user profiles, otherwise use the generic fallback
      return (
        user.publicProfile?.name ||
        user.profile?.name ||
        i18n('match2.tournament.you')
      );
    }
    // Get opponent's name, otherwise use a generic name
    return player.name || i18n('match2.tournament.unknownPlayer');
  }

  private getPlayerAvatar(player: Match2ModelTournamentPlayer) {
    const user = StateObserver.getState().user;
    if (player.id === user.id) {
      return user.publicProfile?.photo ?? user.profile?.photo ?? GENERIC_AVATAR;
    }
    return player.avatar ?? GENERIC_AVATAR;
  }

  private createItem(
    superview: View,
    i: number,
    data: Match2TournamentEntryParams,
  ) {
    const entry = new Match2TournamentEntry(
      { superview, scale: entryScale, width: 570, height: 75 },
      'streak',
    );
    entry.setup({ ...data, position: i + 1 });
    return entry;
  }

  private setItemData(
    i: number,
    data: Match2TournamentEntryParams,
    view: Match2TournamentEntry,
  ) {
    view.setup({ ...data, position: i + 1 });
  }

  public override async fadeIn(): Promise<void> {
    this.tickHandler = this.tick.bind(this);
    engine.addListener('Tick', this.tickHandler);
    return super.fadeIn();
  }

  public override async fadeOut(): Promise<void> {
    engine.removeListener('Tick', this.tickHandler);
    return super.fadeOut();
  }

  private tick() {
    // Not properly initialized yet, probably the async getter isn't ready
    if (this.playerRank == null) {
      this.mainPlayer.hide();
      return;
    }

    if (this.scroll.isItemVisible(this.playerRank)) {
      this.mainPlayer.hide();
      return;
    }

    const top =
      this.scroll.getElementPosition(this.playerRank) <
      this.scroll.viewportUpperBounds;

    this.mainPlayer.updateOpts({
      visible: true,
      y: top
        ? this.scrollArea.style.y - 14
        : this.scrollArea.style.y +
          this.scrollArea.style.height -
          this.mainPlayer.style.height * entryScale +
          3,
    });
  }

  public updateState() {
    if (!this.tournamentData) {
      this.updateLoading();
    } else if (this.tournamentData.finalRank) {
      this.updateClaim();
    } else {
      this.updateOngoing();
    }
  }

  private updateLoading() {
    this.buttonCta!.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();

    this.buttonCta!.setDisabled(true);
  }

  private updateOngoing() {
    this.buttonCta!.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();
    this.buttonCta!.setDisabled(false);
  }

  private updateClaim() {
    if (this.shouldClaimRewards) return;
    this.shouldClaimRewards = true;
    this.buttonClose!.hide();
    this.header.setImage(getStreakTournamentBannerImage('claim'));
    this.title.updateOpts(getStreakPopupClaimTitleLayout());
    // Prioritise current rank for testing purposes - in real scenarios, final rank should be equal to the current rank
    const rank =
      this.tournamentData!.currentRank ?? this.tournamentData!.finalRank ?? 0;
    this.resultRank.showRank(rank);
    this.resultReward.showReward(rank);
    this.timer.updateOpts({ y: 320 });
    this.streak.hide();
    this.message.updateOpts({
      y: 395,
    });
    sounds.sfx.play('applause');
    const claimable = match2TournamentRuleset.getRewardsByRank(rank);
    this.message.localeText = () =>
      i18n(`match2.tournament.message_${claimable ? 'win' : 'lose'}`);
    this.buttonCta!.localeText = () =>
      i18n(
        claimable ? 'match2.tournament.claim' : 'basic.continue',
      ).toLocaleUpperCase();
    const newCtaWidth = 290;
    this.buttonCta!.updateOpts({
      width: newCtaWidth,
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
    });
    this.buttonCta!.label.updateOpts({ width: newCtaWidth - newCtaWidth / 6 });
    this.buttonCta!.setDisabled(false);
  }

  protected getAssetsGroup() {
    // Tournament assets are not organised by type, so we load the whole folder.
    const assetPaths = [`assets/ui/match2/tournament`];
    if (isFlyingHighRedesignTournament('streak')) {
      assetPaths.push(`assets/ui/match2/tournament/flyinghigh`);
    }
    const urls = AssetGroup.constructURLs(assetPaths);
    return new AssetGroup(urls);
  }
}
